<template>
  <main class="lesson-page">
    <template v-if="lesson">
      <header class="lesson-header">
        <div class="header-inner">
          <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zum Modul" @click="returnToModule">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-copy">
            <div class="header-eyebrow">Lektion {{ (lesson.order || 0) + 1 }}</div>
            <h1>{{ lesson.title }}</h1>
          </div>
          <span class="content-badge"
            ><v-icon>{{ getContentIcon(lesson.contentType) }}</v-icon
            >{{ contentLabel(lesson.contentType) }}</span
          >
        </div>
      </header>

      <div class="lesson-content">
        <v-card v-if="lesson.introduction" dark flat class="introduction-card">
          <span><v-icon>mdi-information-outline</v-icon></span>
          <p>{{ lesson.introduction }}</p>
        </v-card>

        <section v-if="isStreamLesson && hasContent" class="media-surface">
          <SecuredMedia
            ref="securedMedia"
            :src="streamPath"
            :media-type="lesson.contentType"
            :stream-playback="lesson.contentType === 'video'"
            :download-name="fileName"
            eager
            @loaded="handleMediaLoaded"
            @error="handleMediaError" />
          <div v-if="lesson.contentType === 'pdf'" class="document-actions">
            <div>
              <strong>{{ fileName }}</strong>
              <span v-if="fileSize">{{ fileSize }}</span>
            </div>
            <v-btn outlined color="#58d388" class="download-action" :loading="downloading" :disabled="downloading" @click="downloadLessonFile">
              <v-icon left>mdi-download-outline</v-icon>PDF herunterladen
            </v-btn>
          </div>
        </section>

        <v-card v-if="lesson.contentType === 'text' && hasContent" dark flat class="reader-card">
          <div class="reader-heading">
            <span><v-icon>mdi-text-box-outline</v-icon></span>
            <div>
              <div>Lesemodus</div>
              <h2>{{ lesson.title }}</h2>
            </div>
          </div>
          <div class="text-content" v-html="safeLessonHtml"></div>
        </v-card>

        <v-card v-if="lesson.contentType === 'file' && hasContent" dark flat class="file-card">
          <span class="file-icon"><v-icon>mdi-file-document-outline</v-icon></span>
          <div class="file-copy">
            <div class="file-eyebrow">Datei-Lektion</div>
            <h2>{{ fileName }}</h2>
            <p v-if="fileSize">{{ fileSize }}</p>
          </div>
          <v-btn color="#009640" depressed class="download-action" :loading="downloading" :disabled="downloading" @click="downloadLessonFile">
            <v-icon left>mdi-download-outline</v-icon>Datei herunterladen
          </v-btn>
        </v-card>

        <v-card v-if="!hasContent" dark flat class="content-message content-missing">
          <v-icon>mdi-file-alert-outline</v-icon>
          <span>Diese Lektion enthält noch keinen verfügbaren Inhalt. Bitte melde dich bei der Academy-Leitung.</span>
        </v-card>

        <v-card v-if="mediaError || downloadError" dark flat class="content-message content-error">
          <v-icon>mdi-alert-circle-outline</v-icon>
          <span>{{ mediaError || downloadError }}</span>
        </v-card>
      </div>

      <footer class="lesson-footer">
        <div class="footer-inner">
          <div class="completion-hint">
            <v-icon :color="canComplete ? '#7ee5a6' : '#a3a3a8'">{{ canComplete ? "mdi-check-circle-outline" : "mdi-clock-outline" }}</v-icon>
            <span>{{ completionHint }}</span>
          </div>
          <v-btn color="#009640" depressed class="complete-action" :disabled="!canComplete" :loading="completing" @click="completeLesson">
            <v-icon left>mdi-check-circle-outline</v-icon>
            Lektion abschliessen
          </v-btn>
        </div>
      </footer>
    </template>

    <div v-else class="loading-surface">
      <v-progress-circular indeterminate color="#58d388" size="48" width="4" />
    </div>
  </main>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

const ALLOWED_TAGS = new Set(["p", "br", "strong", "em", "u", "ul", "ol", "li", "h2", "h3", "h4", "blockquote", "code", "pre", "a"])
const BLOCKED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "svg",
  "math",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "link",
  "meta",
  "base",
  "template",
  "canvas",
  "video",
  "audio",
  "source",
  "track",
  "noscript",
])
const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"])

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function isSafeLink(value) {
  const href = String(value || "").trim()
  if (!href) return false
  if (/[\u0000-\u001f\u007f]/.test(href)) return false
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(href)
  if (!hasScheme && !href.startsWith("//")) return true
  try {
    const base = typeof window !== "undefined" && window.location ? window.location.href : "https://local.invalid/"
    return SAFE_LINK_PROTOCOLS.has(new URL(href, base).protocol.toLowerCase())
  } catch (_) {
    return false
  }
}

function sanitizeChildren(parent) {
  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType === 3) return
    if (node.nodeType !== 1) {
      parent.removeChild(node)
      return
    }

    const tag = node.tagName.toLowerCase()
    if (BLOCKED_TAGS.has(tag)) {
      parent.removeChild(node)
      return
    }

    if (ALLOWED_TAGS.has(tag)) {
      const href = tag === "a" ? node.getAttribute("href") : null
      Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name))
      if (tag === "a" && isSafeLink(href)) {
        node.setAttribute("href", href.trim())
        node.setAttribute("rel", "noopener noreferrer")
      }
      sanitizeChildren(node)
      return
    }

    sanitizeChildren(node)
    while (node.firstChild) parent.insertBefore(node.firstChild, node)
    parent.removeChild(node)
  })
}

function sanitizeLessonHtml(value) {
  const source = String(value || "")
  if (typeof DOMParser === "undefined") return escapeHtml(source)
  try {
    const documentNode = new DOMParser().parseFromString(source, "text/html")
    sanitizeChildren(documentNode.body)
    return documentNode.body.innerHTML
  } catch (_) {
    return escapeHtml(source)
  }
}

function hasVisibleLessonHtml(value) {
  const html = String(value || "")
  if (!html) return false
  if (typeof DOMParser === "undefined")
    return Boolean(
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim(),
    )
  try {
    const documentNode = new DOMParser().parseFromString(html, "text/html")
    return Boolean((documentNode.body.textContent || "").replace(/[\u00a0\u200b-\u200d\ufeff]/g, " ").trim())
  } catch (_) {
    return false
  }
}

export default {
  name: "LessonPlayer",
  components: { SecuredMedia },
  data() {
    return {
      lesson: null,
      moduleItem: null,
      streamPath: "",
      canComplete: false,
      mediaReady: false,
      mediaError: "",
      downloadError: "",
      downloading: false,
      completing: false,
      autoCompleteTimer: null,
    }
  },
  computed: {
    isStreamLesson() {
      return Boolean(this.lesson && ["video", "audio", "image", "pdf"].includes(this.lesson.contentType))
    },
    hasContent() {
      if (!this.lesson) return false
      if (this.lesson.contentType === "text") return hasVisibleLessonHtml(this.safeLessonHtml)
      return Boolean(this.lesson.mediaFile && this.lesson.mediaFile.fileId && this.lesson.mediaFile.size !== 0 && this.streamPath)
    },
    safeLessonHtml() {
      return sanitizeLessonHtml(this.lesson && this.lesson.textContent)
    },
    fileName() {
      return (this.lesson && this.lesson.mediaFile && this.lesson.mediaFile.originalName) || "datei"
    },
    fileSize() {
      const size = this.lesson && this.lesson.mediaFile && this.lesson.mediaFile.size
      return size ? this.formatSize(size) : ""
    },
    completionHint() {
      if (!this.hasContent) return "Der Abschluss ist ohne verfügbaren Inhalt nicht möglich."
      if (this.canComplete) return "Du kannst diese Lektion abschliessen."
      if (this.lesson.contentType === "file") return "Lade die Datei herunter, um den Abschluss freizuschalten."
      if (this.mediaError) return "Der Abschluss bleibt gesperrt, bis der Inhalt geladen werden konnte."
      return this.mediaReady ? "Der Abschluss wird gleich freigeschaltet." : "Der Inhalt wird sicher geladen."
    },
  },
  mounted() {
    this.loadLesson()
  },
  beforeDestroy() {
    if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
  },
  methods: {
    async loadLesson() {
      try {
        this.canComplete = false
        this.mediaReady = false
        this.mediaError = ""
        this.downloadError = ""
        this.streamPath = ""
        if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
        this.autoCompleteTimer = null
        const moduleItem = await api.akademie.fetchModule(this.$route.params.id)
        if (!moduleItem) return
        this.moduleItem = moduleItem
        if (moduleItem.lessons) {
          this.lesson = moduleItem.lessons.find((entry) => entry._id === this.$route.params.lessonId)
        }
        if (this.lesson && this.lesson.mediaFile && this.lesson.mediaFile.fileId) {
          this.streamPath =
            this.lesson.contentType === "video"
              ? api.akademie.getLessonPlaybackPath(this.lesson._id)
              : api.akademie.getLessonStreamPath(this.lesson._id)
        }
        if (this.lesson && this.lesson.contentType === "text" && this.hasContent) {
          this.canComplete = true
        }
      } catch (error) {
        console.error("Fehler beim Laden der Lektion:", error)
      }
    },
    getContentIcon(type) {
      const icons = {
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        image: "mdi-image-outline",
        text: "mdi-text-box-outline",
        pdf: "mdi-file-pdf-box",
        file: "mdi-file-document-outline",
      }
      return icons[type] || "mdi-file-outline"
    },
    contentLabel(type) {
      const labels = { video: "Video", audio: "Audio", image: "Bild", text: "Text", pdf: "PDF", file: "Datei" }
      return labels[type] || "Inhalt"
    },
    handleMediaLoaded() {
      this.mediaReady = true
      this.mediaError = ""
      if (this.lesson.contentType === "image") {
        this.canComplete = true
        return
      }
      if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
      this.autoCompleteTimer = setTimeout(() => {
        this.canComplete = true
        this.autoCompleteTimer = null
      }, 5000)
    },
    handleMediaError() {
      if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
      this.autoCompleteTimer = null
      this.mediaReady = false
      this.canComplete = false
      this.mediaError = "Der Inhalt konnte nicht geladen werden. Bitte versuche es erneut oder melde dich bei der Academy-Leitung."
    },
    async downloadLessonFile() {
      if (!this.hasContent || this.downloading) return
      this.downloading = true
      this.downloadError = ""
      try {
        await api.akademie.downloadLessonMedia(this.lesson._id, this.fileName)
        if (["file", "pdf"].includes(this.lesson.contentType)) {
          if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
          this.autoCompleteTimer = null
          this.mediaReady = true
          this.mediaError = ""
          this.canComplete = true
        }
      } catch (error) {
        this.downloadError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Die Datei konnte nicht heruntergeladen werden. Bitte versuche es erneut."
      } finally {
        this.downloading = false
      }
    },
    formatSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    async completeLesson() {
      this.completing = true
      try {
        await api.akademie.completeLesson(this.$route.params.id, this.lesson._id)
        this.returnToModule()
      } catch (error) {
        console.error("Completion error", error)
      } finally {
        this.completing = false
      }
    },
    returnToModule() {
      this.$router.push({
        path: `/akademie/modules/${this.$route.params.id}`,
        query: this.$route.query.from === "leadership" ? { from: "leadership" } : {},
      })
    },
  },
}
</script>

<style scoped>
.lesson-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding-bottom: 6.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.lesson-header {
  position: sticky;
  z-index: 5;
  top: 0;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: rgba(18, 18, 18, 0.92);
  backdrop-filter: blur(1rem);
  -webkit-backdrop-filter: blur(1rem);
}

.header-inner,
.footer-inner {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
}

.header-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.header-copy {
  min-width: 0;
}

.header-eyebrow,
.reader-heading > div > div {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.header-copy h1 {
  margin: 0.125rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 1.125rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
  font-size: 0.75rem;
  font-weight: 750;
}

.content-badge .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.lesson-content {
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.5rem;
}

.introduction-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem !important;
  background: var(--surface) !important;
}

.introduction-card > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.introduction-card > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.introduction-card p {
  margin: 0;
  color: #d4d4d8;
  font-size: 1rem;
  line-height: 1.65;
}

.media-surface,
.reader-card,
.file-card,
.content-message {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.24);
}

.media-surface ::v-deep .secured-media-content {
  width: 100%;
  max-height: 75vh;
  border-radius: 1.25rem;
}

.reader-card,
.file-card {
  padding: 1.5rem;
}

.reader-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.reader-heading > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.reader-heading > span .v-icon {
  color: currentColor;
}

.reader-heading h2 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.25rem;
}

.text-content {
  padding-top: 1.25rem;
  color: #e4e4e7;
  font-size: 1rem;
  line-height: 1.8;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.document-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid var(--border);
}

.document-actions > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.document-actions strong,
.file-copy h2 {
  overflow: hidden;
  color: #ffffff;
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-actions span,
.file-copy p {
  margin: 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.file-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
}

.file-icon {
  display: grid;
  width: 3.25rem;
  height: 3.25rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.file-icon .v-icon,
.content-message .v-icon {
  color: currentColor;
}

.file-copy {
  min-width: 0;
}

.file-eyebrow {
  margin-bottom: 0.125rem;
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.file-copy h2 {
  margin: 0;
}

.download-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.content-message {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.content-missing {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1) !important;
}

.content-error {
  margin-top: 1rem;
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.1) !important;
}

.lesson-footer {
  position: fixed;
  z-index: 6;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0.75rem 1.5rem max(0.75rem, env(safe-area-inset-bottom));
  border-top: 1px solid var(--border);
  background: rgba(18, 18, 18, 0.94);
  backdrop-filter: blur(1rem);
  -webkit-backdrop-filter: blur(1rem);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.completion-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.complete-action {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.loading-surface {
  display: grid;
  min-height: 100vh;
  min-height: 100dvh;
  place-items: center;
}

@media (max-width: 37.4375rem) {
  .lesson-page {
    padding-bottom: 9rem;
  }

  .lesson-header,
  .lesson-content,
  .lesson-footer {
    padding-right: 0.75rem;
    padding-left: 0.75rem;
  }

  .content-badge {
    width: 2.75rem;
    padding: 0;
    justify-content: center;
  }

  .content-badge .v-icon {
    font-size: 1.25rem;
  }

  .lesson-content {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .reader-card {
    padding: 1rem;
    border-radius: 1.0625rem !important;
  }

  .document-actions,
  .file-card {
    align-items: stretch;
    grid-template-columns: auto minmax(0, 1fr);
    flex-direction: column;
  }

  .document-actions {
    display: flex;
  }

  .file-card .download-action {
    grid-column: 1 / -1;
    width: 100%;
  }

  .footer-inner {
    align-items: stretch;
    flex-direction: column;
    gap: 0.625rem;
  }

  .completion-hint {
    justify-content: center;
    text-align: center;
  }

  .complete-action {
    width: 100%;
  }
}
</style>
